import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Button,
  alpha,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import s3Utils from "../../../utils/s3Utils";

const border = "1px solid #dcdcdc";
const label = { fontWeight: 600, backgroundColor: "#f8f9fa", padding: "8px" };
const sectionHeader = {
  backgroundColor: "#2c3e50",
  color: "white",
  fontWeight: 700,
  textAlign: "center",
  padding: "12px",
};
const subSection = {
  backgroundColor: "#3498db",
  color: "white",
  fontWeight: 700,
  padding: "8px",
};

const TestingWitnessedForm11 = ({ data, laboratoryName, assessmentDate, facility }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  if (!data || data.length === 0) return null;

  const colors = {
    primary: { main: "#1976d2" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0" },
  };

  // Separate testing and retesting
  const testingData = data.filter(test => test.type_witness === "Testing");
  const retestingData = data.filter(test => test.type_witness === "Re-Testing");

  // Group by assessor
  const groupTestsByAssessor = (tests) => {
    return tests.reduce((acc, test) => {
      const assessorName = test.assessorName || "Unknown Assessor";
      if (!acc[assessorName]) {
        acc[assessorName] = [];
      }
      acc[assessorName].push(test);
      return acc;
    }, {});
  };

  const groupedTestingData = groupTestsByAssessor(testingData);
  const groupedRetestingData = groupTestsByAssessor(retestingData);

  // Helper function to get additional test data from scope array
  const getTestScopeData = (testCode, scopeArray) => {
    if (!scopeArray || !testCode) return {};
    const scopeItem = scopeArray.find(scope => scope.testCode === testCode);
    return scopeItem || {};
  };

  const renderTestingSection = (tests, title) => {
    if (!tests || Object.keys(tests).length === 0) return null;

    return (
      <Card elevation={3} sx={{ mb: 4 }}>
        {/* Section Header */}
        <Box sx={sectionHeader}>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {Object.entries(tests).map(([assessorName, assessorTests], assessorIndex) => (
                <React.Fragment key={assessorIndex}>
                  {/* Assessor Header */}
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        border,
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "1rem",
                        py: 2,
                        textAlign: "center"
                      }}
                    >
                      Assessor: {assessorName}
                    </TableCell>
                  </TableRow>

                  {assessorTests.map((test, testIndex) => (
                    <React.Fragment key={testIndex}>
                      {/* Test Number Header */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            border,
                            backgroundColor: "#34495e",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "center",
                            py: 1
                          }}
                        >
                          Test {testIndex + 1}
                        </TableCell>
                      </TableRow>

                      {/* Laboratory Information */}
                      {/* <TableRow>
                        <TableCell sx={{ ...label, border, width: "40%" }}>
                          Name of the Laboratory:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {laboratoryName || test.laboratoryName || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Discipline(s):
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.discipline || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Facility (Permanent/ POCT/ Mobile/ Site facility):
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {facility || test.testPerformedAt || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Date(s) of Assessment:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {assessmentDate || test.assessmentDate || "N/A"}
                        </TableCell>
                      </TableRow> */}

                      <TableRow>
                        <TableCell sx={{ ...label, border, width: "40%" }}>
                          Materials or Products tested:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.groupTested || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Component, parameter or characteristic tested/ Specific Test Performed/ Tests or type of tests performed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.typeOfTest || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Test Method Specification against which tests are performed and/or the techniques/ equipment used:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.scopeStandard || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Range of Testing / Limits of detection:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.rangeOfTesting || test.unit || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          % CV/Measurement Uncertainty expressed as (±) of Observed Value:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.measurementUncertainty || 
                           (test.cv && test.mu ? `CV: ${test.cv}%, MU: ±${test.mu}%` : "N/A")}
                        </TableCell>
                      </TableRow>

                      {/* Testing Details Section */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            ...subSection,
                            border,
                            textAlign: "center"
                          }}
                        >
                          Fresh Sample testing
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Sample ID:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.freshSampleId || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Tested by:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.testConductedBy || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Results of Test Witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.resultOfTestWitnessed1 || test.resultOfTestWitnessed2 || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* Testing of CRM/SRM */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            ...subSection,
                            border,
                            textAlign: "center"
                          }}
                        >
                          Testing of Internal Quality Control(s)
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Level of Internal Quality Control:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.refernceMaterial || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Specified Value:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.specifiedValue || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Results of Test Witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.resultOfTestWitnessed2 || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* Remarks and Conclusion */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            ...subSection,
                            border,
                            textAlign: "center"
                          }}
                        >
                          Remarks
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Deviations observed, if any:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.deviationObserved || "None"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Conclusion on the technical competence of the lab for the test witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: test.conclusion === "competent" ? "success.main" : "error.main",
                              textTransform: "capitalize",
                            }}
                          >
                            {test.conclusion || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Evidence Files */}
                      {test.evidence && test.evidence.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              sx={{
                                ...subSection,
                                border,
                                textAlign: "center"
                              }}
                            >
                              Evidence Files ({test.evidence.length})
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={border}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 2,
                                  p: 1,
                                }}
                              >
                                {test.evidence.map((img, imgIndex) => {
                                  const docLower = img.imgfile.toLowerCase();
                                  const isImage = docLower.match(/\.(jpg|jpeg|png|gif|webp)$/);
                                  const isPdf = docLower.endsWith(".pdf");
                                  const isExcel = docLower.match(/\.(xlsx|xls)$/);

                                  return (
                                    <WorkingSpaceDocument
                                      key={imgIndex}
                                      doc={img.imgfile}
                                      index={imgIndex}
                                      isImage={isImage}
                                      isPdf={isPdf}
                                      isExcel={isExcel}
                                      colors={colors}
                                      onImageClick={isImage ? handleOpenLightbox : null}
                                    />
                                  );
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Divider sx={{ my: 2, backgroundColor: "#34495e", height: 2 }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  const renderRetestingSection = (tests, title) => {
    if (!tests || Object.keys(tests).length === 0) return null;

    return (
      <Card elevation={3} sx={{ mb: 4 }}>
        {/* Section Header */}
        <Box sx={{...sectionHeader, backgroundColor: "#e74c3c"}}>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {Object.entries(tests).map(([assessorName, assessorTests], assessorIndex) => (
                <React.Fragment key={assessorIndex}>
                  {/* Assessor Header */}
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        border,
                        backgroundColor: "#c0392b",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "1rem",
                        py: 2,
                        textAlign: "center"
                      }}
                    >
                      Assessor: {assessorName}
                    </TableCell>
                  </TableRow>

                  {assessorTests.map((test, testIndex) => (
                    <React.Fragment key={testIndex}>
                      {/* Test Number Header */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            border,
                            backgroundColor: "#8e44ad",
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "center",
                            py: 1
                          }}
                        >
                          Retest {testIndex + 1}
                        </TableCell>
                      </TableRow>

                      {/* Laboratory Information */}
                      {/* <TableRow>
                        <TableCell sx={{ ...label, border, width: "40%" }}>
                          Name of the Laboratory:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {laboratoryName || test.laboratoryName || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Discipline(s):
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.discipline || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Facility (Permanent/ POCT/ Mobile/ Site facility):
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {facility || test.testPerformedAt || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Date(s) of Assessment:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {assessmentDate || test.assessmentDate || "N/A"}
                        </TableCell>
                      </TableRow> */}

                      <TableRow>
                        <TableCell sx={{ ...label, border, width: "40%"  }}>
                          Materials or Products tested:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.groupTested || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Component, parameter or characteristic tested/ Specific Test Performed/ Tests or type of tests performed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.typeOfTest || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Test Method Specification against which tests are performed and/or the techniques/ equipment used:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.scopeStandard || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Range of Testing / Limits of detection:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.rangeOfTesting || test.unit || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          % CV/Measurement Uncertainty expressed as (±) of Observed Value:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.measurementUncertainty || 
                           (test.cv && test.mu ? `CV: ${test.cv}%, MU: ±${test.mu}%` : "N/A")}
                        </TableCell>
                      </TableRow>

                      {/* Re-Testing/Replicate Details */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            backgroundColor: "#e67e22",
                            color: "white",
                            fontWeight: 700,
                            padding: "8px",
                            border,
                            textAlign: "center"
                          }}
                        >
                          Re-testing testing of Retained Sample 
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Sample ID:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.freshSampleId || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Date of Earlier Testing:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.earlierTestdate || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Earlier Tested by:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.earlierTestedBy || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Earlier Reported Results:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.earlierReportedResults || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Results of Test Witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.resultOfTestWitnessed1 || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Test conducted by (Same Person) Yes/No:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.resultOfTestWitnessedSamePerson === "yes" ? "Yes" : "No"}
                        </TableCell>
                      </TableRow>

                      {test.testConductedByDifferentPerson && (
                        <TableRow>
                          <TableCell sx={{ ...label, border }}>
                            Name of the person in case No:
                          </TableCell>
                          <TableCell sx={{ border }}>
                            {test.testConductedByDifferentPerson}
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Testing of CRM/SRM */}
                      {/* <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            backgroundColor: "#e67e22",
                            color: "white",
                            fontWeight: 700,
                            padding: "8px",
                            border,
                            textAlign: "center"
                          }}
                        >
                          Testing of CRM / SRM
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Level of Internal Quality Control:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.refernceMaterial || "N/A"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Specified Value:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.specifiedValue || "N/A"}
                        </TableCell>
                      </TableRow> */}

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Results of Test Witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.resultOfTestWitnessed2 || "N/A"}
                        </TableCell>
                      </TableRow>

                      {/* Remarks and Conclusion */}
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            backgroundColor: "#e67e22",
                            color: "white",
                            fontWeight: 700,
                            padding: "8px",
                            border,
                            textAlign: "center"
                          }}
                        >
                          Remarks
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Deviations observed, if any:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          {test.deviationObserved || "None"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ ...label, border }}>
                          Conclusion on the technical competence of the lab for the test witnessed:
                        </TableCell>
                        <TableCell sx={{ border }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: test.conclusion === "competent" ? "success.main" : "error.main",
                              textTransform: "capitalize",
                            }}
                          >
                            {test.conclusion || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Evidence Files */}
                      {test.evidence && test.evidence.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              sx={{
                                backgroundColor: "#e67e22",
                                color: "white",
                                fontWeight: 700,
                                padding: "8px",
                                border,
                                textAlign: "center"
                              }}
                            >
                              Evidence Files ({test.evidence.length})
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={border}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 2,
                                  p: 1,
                                }}
                              >
                                {test.evidence.map((img, imgIndex) => {
                                  const docLower = img.imgfile.toLowerCase();
                                  const isImage = docLower.match(/\.(jpg|jpeg|png|gif|webp)$/);
                                  const isPdf = docLower.endsWith(".pdf");
                                  const isExcel = docLower.match(/\.(xlsx|xls)$/);

                                  return (
                                    <WorkingSpaceDocument
                                      key={imgIndex}
                                      doc={img.imgfile}
                                      index={imgIndex}
                                      isImage={isImage}
                                      isPdf={isPdf}
                                      isExcel={isExcel}
                                      colors={colors}
                                      onImageClick={isImage ? handleOpenLightbox : null}
                                    />
                                  );
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Divider sx={{ my: 2, backgroundColor: "#8e44ad", height: 2 }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Testing Section */}
      {renderTestingSection(groupedTestingData, "TESTING")}
      
      {/* Retesting Section */}
      {renderRetestingSection(groupedRetestingData, "RETESTING")}

      {/* Lightbox for image preview */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={handleCloseLightbox}
        >
          <Box sx={{ maxWidth: "90%", maxHeight: "90%" }}>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              bgcolor: "white",
              color: "black",
              "&:hover": { bgcolor: "grey.300" },
            }}
            onClick={handleCloseLightbox}
          >
            Close
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Working Space Document Component to handle images and PDF/Excel thumbnails
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For PDF/Excel files, show thumbnail with icon
  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  // For images, show the actual image
  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

export default TestingWitnessedForm11;